import { useEffect, useRef, useState } from "react";
import { RECEIVE_IDEAS, sendSeekOffset, sendVideoPlaybackState } from "src/pods/ideas";
import { useToolkitIdeasStore } from "src/pods/ideas/toolkitIdeasStore";
import { Slide } from "src/pods/ideas/data/types";
import { displayClose as displayCloseFn, displayOpen as displayOpenFn } from "src/pods/realtime";
import { useToolkitRealtimeStore } from "src/pods/realtime/toolkitRealtimeStore";
import { dispatchToolkitReduxAction } from "src/state/toolkitReduxStore";
import logEvent from "src/utils/logEvent";

export type UseWatchVideoContainerProps = {
  displayOpen: typeof displayOpenFn;
  displayClose: typeof displayCloseFn;
  watch: Slide;
  onBack: VoidFunction;
};

export type UseWatchVideoContainerResult = {
  shouldPlayOnPhone: boolean;
  isCasting: boolean;
  isConnected: boolean;
  isPlaying: boolean | undefined;
  handlers: {
    onPlayOnPhone: VoidFunction;
    onPlayOnComputer: VoidFunction;
    onTogglePlay: VoidFunction;
    onForward: (time: number) => void;
    onReplay: (time: number) => void;
    onRequestStart: VoidFunction;
    onRequestHide: VoidFunction;
    onRequestStop: VoidFunction;
  };
};

/**
 * ViewModel for WatchVideoContainer: encapsulates the cross-store realtime +
 * ideas reads, the action dispatches, and the cast-open/close lifecycle. Pure
 * `WatchVideoContainer` consumes the result and renders the right child
 * (PhoneWatchVideo vs WatchVideo) based on cast + phone-fallback state.
 *
 * Storybook stories override `useVm` via `WatchVideoContainer`'s `di` prop to
 * supply canned state without touching the real stores.
 */
export function useWatchVideoContainer({
  displayOpen,
  displayClose,
  watch,
  onBack,
}: UseWatchVideoContainerProps): UseWatchVideoContainerResult {
  const [shouldPlayOnPhone, setShouldPlayOnPhone] = useState(false);
  const { paired: isConnected, displayOn: isCasting } = useToolkitRealtimeStore();
  const {
    display: { isPlaying, seekOffset },
  } = useToolkitIdeasStore();

  const displayOpenRef = useRef(displayOpen);
  displayOpenRef.current = displayOpen;
  const displayCloseRef = useRef(displayClose);
  displayCloseRef.current = displayClose;

  const openCastedVideo = () => {
    displayOpen("/apps/display/ideas", {
      type: RECEIVE_IDEAS,
      backgroundOverlay: true,
      payload: { question: null, watch, isPlaying: false },
    });
  };

  useEffect(() => {
    logEvent({ eventName: "ideas.setup.open" });
    return () => {
      logEvent({ eventName: "ideas.setup.close" });
      dispatchToolkitReduxAction(sendVideoPlaybackState(false));
      displayCloseRef.current();
    };
  }, []);

  useEffect(() => {
    displayOpenRef.current("/apps/display/ideas", {
      type: RECEIVE_IDEAS,
      backgroundOverlay: true,
      payload: { question: null, watch, isPlaying: false },
    });
  }, [watch]);

  return {
    shouldPlayOnPhone,
    isCasting,
    isConnected,
    isPlaying,
    handlers: {
      onPlayOnPhone: () => {
        setShouldPlayOnPhone(true);
        displayClose();
      },
      onPlayOnComputer: () => {
        setShouldPlayOnPhone(false);
        openCastedVideo();
      },
      onTogglePlay: () => {
        dispatchToolkitReduxAction(sendVideoPlaybackState(!isPlaying));
      },
      onForward: (timeInSeconds: number) => {
        dispatchToolkitReduxAction(sendSeekOffset(seekOffset + timeInSeconds));
      },
      onReplay: (timeInSeconds: number) => {
        dispatchToolkitReduxAction(sendSeekOffset(seekOffset - timeInSeconds));
      },
      onRequestStart: () => {
        setShouldPlayOnPhone(false);
        openCastedVideo();
      },
      onRequestHide: () => {
        displayClose();
        onBack();
      },
      onRequestStop: onBack,
    },
  };
}
